/**
 * Collection View Types
 * 
 * Shared type definitions for the Collection View design system.
 * Supports zero/one/few/many state handling, faceted search, and bulk actions.
 */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

// ============================================================================
// Item Count States
// ============================================================================

/**
 * Represents the quantity state of a collection.
 * Used to adapt UI behavior based on how many items are present.
 */
export type ItemCountState = 'zero' | 'one' | 'few' | 'many';

/**
 * Get the item count state based on the number of items.
 * - zero: 0 items
 * - one: 1 item
 * - few: 2-7 items
 * - many: 8+ items
 */
export function getItemCountState(count: number): ItemCountState {
  if (count === 0) return 'zero';
  if (count === 1) return 'one';
  if (count <= 7) return 'few';
  return 'many';
}

// ============================================================================
// View Modes
// ============================================================================

export type ViewMode = 'list' | 'card' | 'table' | 'kanban';

export interface ViewModeConfig {
  mode: ViewMode;
  icon: LucideIcon;
  label: string;
}

// ============================================================================
// Sorting
// ============================================================================

export type SortDirection = 'asc' | 'desc';

export interface SortOption<T = string> {
  field: T;
  label: string;
  defaultDirection?: SortDirection;
}

export interface SortState<T = string> {
  field: T;
  direction: SortDirection;
}

// ============================================================================
// Grouping
// ============================================================================

export interface GroupOption<T = string> {
  field: T;
  label: string;
  /** Function to get group key from item */
  getGroupKey?: (item: unknown) => string;
  /** Function to get group label from key */
  getGroupLabel?: (key: string) => string;
}

export interface GroupState<T = string> {
  field: T | null;
  expandedGroups: Set<string>;
}

export interface GroupedItems<T> {
  key: string;
  label: string;
  items: T[];
  count: number;
}

// ============================================================================
// Filtering (Faceted Search)
// ============================================================================

export type FilterOperator = 
  | 'equals' 
  | 'not_equals' 
  | 'contains' 
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'in'
  | 'not_in'
  | 'is_empty'
  | 'is_not_empty';

export interface FacetOption {
  value: string;
  label: string;
  count?: number;
  icon?: LucideIcon;
  color?: string;
}

export interface FacetDefinition<T = string> {
  /** Unique identifier for this facet */
  key: T;
  /** Display label */
  label: string;
  /** Available options (can be static or dynamic) */
  options: FacetOption[];
  /** Allow multiple selections */
  multi?: boolean;
  /** Icon for the facet */
  icon?: LucideIcon;
  /** Supported operators (default: ['equals']) */
  operators?: FilterOperator[];
  /** Whether this facet is searchable */
  searchable?: boolean;
}

export interface ActiveFilter<T = string> {
  id: string;
  facetKey: T;
  operator: FilterOperator;
  value: string | string[];
  label: string;
}

// ============================================================================
// Selection
// ============================================================================

export interface SelectionState {
  selectedIds: Set<string>;
  lastSelectedId: string | null;
  isAllSelected: boolean;
  isIndeterminate: boolean;
}

export interface SelectionActions {
  select: (id: string) => void;
  deselect: (id: string) => void;
  toggle: (id: string) => void;
  toggleRange: (id: string, shiftKey: boolean) => void;
  selectAll: () => void;
  deselectAll: () => void;
  setSelected: (ids: string[]) => void;
}

// ============================================================================
// Bulk Actions
// ============================================================================

export type BulkActionVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

export interface BulkAction {
  id: string;
  label: string;
  icon?: LucideIcon;
  variant?: BulkActionVariant;
  /** Called with array of selected item IDs */
  onClick: (selectedIds: string[]) => void | Promise<void>;
  /** Disable this action conditionally */
  disabled?: boolean | ((selectedIds: string[]) => boolean);
  /** Show confirmation dialog before executing */
  confirmMessage?: string;
}

// ============================================================================
// Collection State
// ============================================================================

export interface CollectionState<T, SortField = string, FilterField = string, GroupField = string> {
  /** All items in the collection */
  items: T[];
  /** Items after filtering */
  filteredItems: T[];
  /** Items after filtering and sorting */
  sortedItems: T[];
  /** Items grouped (if grouping is active) */
  groupedItems: GroupedItems<T>[] | null;
  
  // View
  viewMode: ViewMode;
  
  // Search
  searchQuery: string;
  
  // Filtering
  activeFilters: ActiveFilter<FilterField>[];
  
  // Sorting
  sort: SortState<SortField>;
  
  // Grouping
  group: GroupState<GroupField>;
  
  // Selection
  selection: SelectionState;
  
  // Derived states
  itemCount: number;
  filteredCount: number;
  itemCountState: ItemCountState;
}

// ============================================================================
// Collection Actions
// ============================================================================

export interface CollectionActions<T, SortField = string, FilterField = string, GroupField = string> {
  // View
  setViewMode: (mode: ViewMode) => void;
  
  // Search
  setSearchQuery: (query: string) => void;
  
  // Filtering
  addFilter: (filter: Omit<ActiveFilter<FilterField>, 'id'>) => void;
  removeFilter: (filterId: string) => void;
  clearFilters: () => void;
  setFilters: (filters: ActiveFilter<FilterField>[]) => void;
  
  // Sorting
  setSort: (field: SortField, direction?: SortDirection) => void;
  toggleSortDirection: () => void;
  
  // Grouping
  setGroupBy: (field: GroupField | null) => void;
  toggleGroup: (groupKey: string) => void;
  expandAllGroups: () => void;
  collapseAllGroups: () => void;
  
  // Selection
  selection: SelectionActions;
  
  // Items
  setItems: (items: T[]) => void;
}

// ============================================================================
// Collection View Props
// ============================================================================

export interface CollectionViewProps<T, SortField = string, FilterField = string, GroupField = string> {
  /** Items to display */
  items: T[];
  /** Extract unique ID from item */
  getId: (item: T) => string;
  
  // Rendering
  /** Render item in list view */
  renderItem: (item: T, props: ItemRenderProps) => ReactNode;
  /** Render item in card view */
  renderCard?: (item: T, props: ItemRenderProps) => ReactNode;
  /** Render item in table row */
  renderTableRow?: (item: T, props: ItemRenderProps) => ReactNode;
  /** Table column headers (for table view) */
  tableColumns?: TableColumn[];
  /** Table columns with render functions (for virtualized table view) */
  tableColumnsWithRender?: TableColumnWithRender<T>[];
  
  // Empty state
  emptyVariant?: 'ideas' | 'items' | 'cycles' | 'focus_lists' | 'team' | 'results' | 'search' | 'generic';
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: {
    label: string;
    onClick?: () => void;
    path?: string;
  };
  
  // Filtering
  facets?: FacetDefinition<FilterField>[];
  searchFields?: (keyof T)[];
  searchPlaceholder?: string;
  
  // Sorting
  sortOptions?: SortOption<SortField>[];
  defaultSort?: SortState<SortField>;
  
  // Grouping
  groupOptions?: GroupOption<GroupField>[];
  defaultGroupBy?: GroupField;
  
  // Selection
  selectable?: boolean;
  bulkActions?: BulkAction[];
  
  // View modes
  viewModes?: ViewMode[];
  defaultViewMode?: ViewMode;
  
  // Virtualization
  /** Enable virtualization for large lists (auto-enabled when items > virtualizationThreshold) */
  virtualize?: boolean;
  /** Item count threshold for auto-virtualization (default: 100) */
  virtualizationThreshold?: number;
  /** Estimated row height for virtualization */
  estimatedRowHeight?: number;
  /** Height of the virtualized container */
  virtualizedHeight?: number | string;
  
  // Keyboard navigation
  /** Enable keyboard navigation (j/k, Enter) */
  enableKeyboardNavigation?: boolean;
  
  // Pagination
  pageSize?: number;
  
  // Layout
  className?: string;
  toolbarClassName?: string;
  contentClassName?: string;
  /** Hide built-in toolbar when using EntityListShell / item-list header */
  hideToolbar?: boolean;
  /** Hide the "N of M items" stats row below the toolbar */
  hideStatsBar?: boolean;
  /** Controlled collection state (requires hideToolbar) */
  controlledState?: CollectionState<T, SortField, FilterField, GroupField>;
  /** Controlled collection actions (requires hideToolbar) */
  controlledActions?: CollectionActions<T, SortField, FilterField, GroupField>;
  /** 'cards' = individual bordered cards per row (default); 'rows' = unified container with divide-y */
  listStyle?: 'cards' | 'rows';
  
  // Callbacks
  onSelectionChange?: (selectedIds: string[]) => void;
  onItemClick?: (item: T) => void;
  onItemDoubleClick?: (item: T) => void;
}

export interface ItemRenderProps {
  selected: boolean;
  onSelect: () => void;
  onToggle: () => void;
  highlighted?: boolean;
  disabled?: boolean;
}

export interface TableColumn {
  key: string;
  label: string;
  width?: string | number;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
}

/**
 * Table column with render function for virtualized table view
 */
export interface TableColumnWithRender<T> extends TableColumn {
  /** Render cell content */
  render: (item: T, props: ItemRenderProps) => ReactNode;
  /** Minimum column width (also used as this column's floor when flex is set) */
  minWidth?: number;
  /** Maximum column width */
  maxWidth?: number;
  /** Whether column is resizable */
  resizable?: boolean;
  /**
   * Grow to fill remaining row width when there's room; shrink first (down to
   * minWidth) when space is tight. Every other column stays its fixed size
   * regardless of viewport. Use on the one identifying/name-like column per
   * table — never on badge/count/status columns, which should stay compact.
   */
  flex?: boolean;
}

// ============================================================================
// Component Props
// ============================================================================

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  showAdvanced?: boolean;
  onAdvancedClick?: () => void;
  className?: string;
}

export interface FilterBarProps<T = string> {
  facets: FacetDefinition<T>[];
  activeFilters: ActiveFilter<T>[];
  onFilterChange: (filters: ActiveFilter<T>[]) => void;
  className?: string;
}

export interface SortGroupControlsProps<SortField = string, GroupField = string> {
  // Sort
  sortOptions: SortOption<SortField>[];
  currentSort: SortState<SortField>;
  onSortChange: (field: SortField, direction?: SortDirection) => void;
  
  // Group
  groupOptions?: GroupOption<GroupField>[];
  currentGroupBy: GroupField | null;
  onGroupByChange: (field: GroupField | null) => void;
  
  className?: string;
}

export interface ViewModeToggleProps {
  modes: ViewMode[];
  currentMode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  className?: string;
}

export interface SelectableListProps<T> {
  items: T[];
  getId: (item: T) => string;
  renderItem: (item: T, props: ItemRenderProps) => ReactNode;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  className?: string;
  /** 'cards' = individual bordered cards (default); 'rows' = unified container with divide-y rows */
  listStyle?: 'cards' | 'rows';
}

export interface BulkActionToolbarProps {
  selectedCount: number;
  totalCount: number;
  actions: BulkAction[];
  onSelectAll: () => void;
  onClearSelection: () => void;
  className?: string;
}
